<template>
  <div class="fixed inset-0 -z-10 overflow-hidden" style="margin-left:-18rem">
    <img class="bg absolute inset-0 object-cover h-full w-full"
         src="/images/bg/haldex-europe.webp" alt="background-image"/>
    <div class="bg-overlay absolute inset-0 opacity-10 bg-gradient-to-l from-surface-900 to-surface-300"></div>
  </div>
  <div class="flex flex-col items-center justify-start z-10 min-h-screen w-full relative py-8 overflow-y-auto">
    <Container>
      <h1 data-cy="welcome-to-sums-display" class="font-bold text-right text-6xl text-white  my-6">{{ $t("pages.home.welcome_to_sums") }}</h1>
      <div class="flex flex-col items-center content-center justify-center">
        <Card class="w-full max-w-3xl">
          <template #content>
            <div class="flex flex-row space-x-3 justify-center items-center py-4" style="padding-top: 0">
              <a :href="supportUrl" target="_blank" rel="noopener noreferrer">
                <Button data-cy="report-a-bug-button" :label="$t('pages.home.report_a_bug')" size="large">
                  <template #icon>
                    <IconBug class="w-5 h-5 mr-1.5 hidden md:block" :stroke-width="2"/>
                  </template>
                </Button>
              </a>

              <a :href="requestFeatureUrl" target="_blank" rel="noopener noreferrer">
                <Button data-cy="request-a-feature-button" :label="$t('pages.home.request_a_feature')" size="large">
                  <template #icon>
                    <IconSparkles class="w-5 h-5 mr-1.5 hidden md:block" :stroke-width="2"/>
                  </template>
                </Button>
              </a>

              <Button
                data-cy="request-for-training-button"
                @click="showTrainingDialog = true"
                :label="$t('pages.home.request_training')"
                size="large"
              >
                <template #icon>
                  <IconBook class="w-5 h-5 mr-1.5 hidden md:block" :stroke-width="2"/>
                </template>
              </Button>

              <a :href="userGuideUrl" target="_blank" rel="noopener noreferrer">
                <Button data-cy="user-guide-button" :label="$t('pages.home.user_guide')" size="large">
                  <template #icon>
                    <IconHeadset class="w-5 h-5 mr-1.5 hidden md:block" :stroke-width="2"/>
                  </template>
                </Button>
              </a>
            </div>
          </template>
        </Card>

        <Card v-if="recentUpdates.length > 0" class="w-full max-w-3xl mt-6">
          <template #title>
        <div class="flex items-center">
          <IconClock class="w-6 h-6 mr-2" :stroke-width="1.5"/>
          Recent Updates
        </div>
          </template>
          <template #content>
        <div class="flex gap-4">
          <!-- Left sidebar with dates (33% width) -->
          <div class="w-1/3 border-r border-surface-200 pr-4">
            <div class="space-y-2">
              <div 
                v-for="update in recentUpdates" 
                :key="update.id"
                @click="selectedUpdate = update"
                :class="[
                  'p-3 rounded-lg cursor-pointer transition-colors duration-200',
                  selectedUpdate?.id === update.id 
                    ? 'bg-primary-500 text-white border border-primary-600 shadow-md' 
                    : 'bg-surface-50 hover:bg-surface-100 border border-surface-200'
                ]"
              >
                <div class="font-semibold text-sm" v-html="update.date"></div>
                <!-- <div :class="[
                  'text-xs mt-1',
                  selectedUpdate?.id === update.id ? 'text-white/80' : 'text-surface-600'
                ]">{{ update.title }}</div> -->
              </div>
            </div>
          </div>
          
          <!-- Right content area (66% width) -->
          <div class="w-2/3 pl-4">
            <div v-if="selectedUpdate">
              <h3 class="font-bold text-lg mb-2">{{ selectedUpdate.title }}</h3>
              <p class="text-sm text-surface-600 mb-3" v-html="selectedUpdate.date"></p>
              <div class="text-surface-800 selectedUpdate" v-html="selectedUpdate.content"></div>
            </div>
            <div v-else class="text-surface-500 text-center py-8">
              Select an update to view details
            </div>
          </div>
        </div>
          </template>
        </Card>
      </div>
    </Container>

    <TrainingRequestDialog v-model="showTrainingDialog" />
  </div>
</template>

<script>
import {IconBug, IconPlus, IconSparkles, IconHeadset, IconBook, IconClock} from '@tabler/icons-vue';
import Container from "@/Components/Container.vue";
import TrainingRequestDialog from '@/Dialogs/TrainingRequestDialog.vue';

export default {
  name: "HomeView",
  data() {
    return {
      value: null,
      supportUrl: import.meta.env.VITE_CUSTOMER_SUPPORT_URL,
      requestFeatureUrl: import.meta.env.VITE_CUSTOMER_SUPPORT_URL,
      userGuideUrl: import.meta.env.VITE_USER_GUIDE_URL,
      selectedUpdate: null,
      recentUpdates: []
    }
  },
  async mounted() {
    await this.fetchUpdates();
    // Set default selection to first update
    if (this.recentUpdates.length > 0) {
      this.selectedUpdate = this.recentUpdates[0];
    }
  },
  methods: {
    async fetchUpdates() {
      try {
        const response = await axios.get('/updates');
        this.recentUpdates = response.data;
      } catch (error) {
        console.error('Failed to fetch updates:', error);
      }
    }
  },
  components: {
    Container,
    IconSparkles,
    IconBug,
    IconHeadset,
    IconBook,
    TrainingRequestDialog,
    IconClock,
  },
}
</script>

<style scoped>
.selectedUpdate :deep(h1) {
  font-size: 2rem;
  font-weight: 700;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}
.selectedUpdate :deep(hr) {
  margin:1rem 0;
}

.selectedUpdate :deep(h2) {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1.25rem;
  margin-bottom: 0.75rem;
}

.selectedUpdate :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.selectedUpdate :deep(ul) {
  margin-left:1.2rem;
}
.selectedUpdate :deep(li) {
  list-style: circle;
}
</style>